import { remember, serializeDataToCacheKey } from '@tuic/cache';
import { logger } from '@tuic/logger';

import { Pcl } from '../../index';
import { CacheTag } from '../../utils';

import type { VenueCategory, VenueCategoryDetail } from '../../generated/pcl';
import type { Locale } from '@tuic/i18n';

/**
 * Fetches a ship's venue categories from the PCL API.
 */
export const getShipVenueCategories = async (
  shipCode: string,
  locale: Locale,
): Promise<VenueCategory[]> => {
  const { data, error } = await Pcl.getShipsByShipCodeVenueCategories({
    path: { shipCode },
    query: { locale },
  });

  if (error) {
    logger.error(
      `Could not fetch ship venue categories. Ship: ${shipCode}, Locale: ${locale}`,
    );
    logger.error(error);

    return [];
  }

  return data ?? [];
};

/**
 * Fetches a ship's venue categories from the PCL API.
 */
export const cachedGetShipVenueCategories = (
  shipCode: string,
  locale: Locale,
) =>
  remember(
    serializeDataToCacheKey([
      CacheTag.pcl,
      'getShipVenueCategories',
      locale,
      shipCode,
    ]),
    () => getShipVenueCategories(shipCode, locale),
    {
      tags: [
        CacheTag.pcl,
        CacheTag.shipResource,
        CacheTag.getShipVenueCategories,
      ],
      ttl: 600,
    },
  );

/**
 * Fetches details about the given venueCategoryCode on given ship.
 */
export const getShipVenueCategory = async (
  shipCode: string,
  venueCategoryCode: string,
  locale: Locale,
): Promise<VenueCategoryDetail | null> => {
  const { data, error } =
    await Pcl.getShipsByShipCodeVenueCategoriesByVenueCategoryCode({
      path: {
        shipCode,
        venueCategoryCode,
      },
      query: {
        locale,
      },
    });

  if (error) {
    logger.error(
      `Could not fetch ship venue category. Ship: ${shipCode}, VenueCategory: ${venueCategoryCode}, Locale: ${locale}`,
    );
    logger.error(error);

    return null;
  }

  return data ?? null;
};

/**
 * Fetches details about the given venueCategoryCode on given ship.
 */
export const cachedGetShipVenueCategory = (
  shipCode: string,
  venueCategoryCode: string,
  locale: Locale,
): Promise<VenueCategoryDetail | null> =>
  remember(
    serializeDataToCacheKey([
      CacheTag.pcl,
      'cachedGetShipVenueCategory',
      shipCode,
      venueCategoryCode,
      locale,
    ]),
    () => getShipVenueCategory(shipCode, venueCategoryCode, locale),
    {
      tags: [
        CacheTag.pcl,
        CacheTag.shipResource,
        CacheTag.getShipVenueCategory,
      ],
      ttl: 600,
    },
  );
